import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, AlertTriangle, Calculator, Plus, Trash2, Edit } from 'lucide-react';
import { riskService } from '../../services/riskService';
import { 
  createDefaultRisk, 
  createBiologicalEntityAssociation,
  CRITICALITY_LEVELS
} from '../../types/risk';
import { useBiologicalEntities } from '../../hooks/useBiologicalEntities';
import { useRiskTypes } from '../../hooks/useNomenclatures';
import { useProcesses } from '../../hooks/useProcesses';
import { useStandardTemplates } from '../../hooks/useQmsStandards';
import { useProtocols } from '../../hooks/useProtocols';
import { usePatientRiskFactors } from '../../hooks/usePatientRiskFactors';
import { useEnvironmentalRiskFactors } from '../../hooks/useEnvironmentalRiskFactors';
import { riskCalculationService } from '../../services/riskCalculationService';
import { riskControlService } from '../../services/riskControlService';
import {
  RISK_LEVEL,
  RISK_CONTROL_TYPES,
  RISK_CONTROL_STATUS,
  RISK_CONTROL_TYPE_OPTIONS,
  RISK_CONTROL_STATUS_OPTIONS,
  THRESHOLD_OPERATORS,
  THRESHOLD_METRICS,
  THRESHOLD_OPERATOR_OPTIONS,
  THRESHOLD_METRIC_OPTIONS
} from '../../types/risk';
import { useRiskControls } from '../../hooks/useRiskControls';
import { useRiskThresholds } from '../../hooks/useRiskThresholds';
import { riskThresholdService } from '../../services/riskThresholdService';
import './RiskEditor.css';

const RiskEditor = ({
  protocol,
  risk,
  stepId,
  actionId,
  level,
  organizationId,
  onClose,
  onSave,
  userId: editorUserId,
  userRole = 'administration'
}) => {
  const { t } = useTranslation(['risks', 'common']);
  const { biological_entities, loading: entitiesLoading } = useBiologicalEntities();
  const { items: riskTypes, loading: riskTypesLoading } = useRiskTypes(true);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isStandalone = level === 'standalone';
  const { processes: orgProcesses } = useProcesses(isStandalone ? organizationId : null);
  const { templates: standardTemplates } = useStandardTemplates();
  // Combined: org instances first, then templates (so dropdown populates even when org has no processes yet)
  const processes = isStandalone
    ? [...(orgProcesses || []), ...(standardTemplates || [])]
    : (orgProcesses || []);
  const { protocols } = useProtocols(isStandalone ? { status: 'active' } : {});
  const { factors: patientRiskFactors } = usePatientRiskFactors(isStandalone ? { activeOnly: true } : {});
  const { factors: environmentalRiskFactors } = useEnvironmentalRiskFactors(isStandalone ? {} : {});
  const { controls, loading: controlsLoading, refresh: refreshControls } = useRiskControls(risk?.id || null);
  const { thresholds, violations, loading: thresholdsLoading, refresh: refreshThresholds } = useRiskThresholds(risk?.id || null);
  const [showControlModal, setShowControlModal] = useState(false);
  const [editingControlId, setEditingControlId] = useState(null);
  const [deleteControlConfirm, setDeleteControlConfirm] = useState(null);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [editingThresholdId, setEditingThresholdId] = useState(null);
  const [deleteThresholdConfirm, setDeleteThresholdConfirm] = useState(null);
  const [thresholdForm, setThresholdForm] = useState({
    metric: THRESHOLD_METRICS.COMPLIANCE_PCT,
    operator: THRESHOLD_OPERATORS.LTE,
    value: '',
    isActive: true
  });
  const [controlForm, setControlForm] = useState({
    controlType: RISK_CONTROL_TYPES.PROTOCOL,
    referenceLabel: '',
    effectivenessTarget: '',
    effectivenessCurrent: '',
    controlStatus: RISK_CONTROL_STATUS.ACTIVE
  });
  const [oScoreReviewSubmitting, setOScoreReviewSubmitting] = useState(false);

  const canView = ['management', 'ic_personnel', 'administration'].includes(userRole);
  const canReview = ['management', 'ic_personnel'].includes(userRole);

  const handleSaveControl = async () => {
    const userId = 'current-user';
    const payload = {
      riskId: risk.id,
      riskSource: 'protocol_risks',
      controlType: controlForm.controlType,
      referenceType: 'text',
      referenceLabel: controlForm.referenceLabel.trim() || null,
      effectivenessTarget: controlForm.effectivenessTarget === '' ? null : Number(controlForm.effectivenessTarget),
      effectivenessCurrent: controlForm.effectivenessCurrent === '' ? null : Number(controlForm.effectivenessCurrent),
      controlStatus: controlForm.controlStatus,
      organizationId: formData.organizationId || organizationId || null
    };
    try {
      if (editingControlId) {
        await riskControlService.updateControl(editingControlId, {
          controlType: payload.controlType,
          referenceLabel: payload.referenceLabel,
          effectivenessTarget: payload.effectivenessTarget,
          effectivenessCurrent: payload.effectivenessCurrent,
          controlStatus: payload.controlStatus
        }, userId);
      } else {
        await riskControlService.createControl(payload, userId);
      }
      setShowControlModal(false);
      setEditingControlId(null);
      refreshControls();
    } catch (err) {
      console.error('Save control:', err);
    }
  };

  const confirmDeleteControl = async () => {
    if (!deleteControlConfirm) return;
    try {
      await riskControlService.deleteControl(deleteControlConfirm.id);
      setDeleteControlConfirm(null);
      refreshControls();
    } catch (err) {
      console.error('Delete control:', err);
    }
  };

  const handleSaveThreshold = async () => {
    const userId = 'current-user';
    const value = thresholdForm.value === '' ? null : Number(thresholdForm.value);
    if (value === null && !editingThresholdId) return;
    try {
      if (editingThresholdId) {
        await riskThresholdService.updateThreshold(editingThresholdId, {
          metric: thresholdForm.metric,
          operator: thresholdForm.operator,
          value,
          isActive: thresholdForm.isActive
        }, userId);
      } else {
        await riskThresholdService.createThreshold({
          riskId: risk.id,
          metric: thresholdForm.metric,
          operator: thresholdForm.operator,
          value,
          isActive: thresholdForm.isActive
        }, userId);
      }
      setShowThresholdModal(false);
      setEditingThresholdId(null);
      refreshThresholds();
    } catch (err) {
      console.error('Save threshold:', err);
    }
  };

  const confirmDeleteThreshold = async () => {
    if (!deleteThresholdConfirm) return;
    try {
      await riskThresholdService.deleteThreshold(deleteThresholdConfirm.id);
      setDeleteThresholdConfirm(null);
      refreshThresholds();
    } catch (err) {
      console.error('Delete threshold:', err);
    }
  };

  // Initialize form data
  useEffect(() => {
    if (risk) {
      // Editing existing risk
      setFormData({
        ...risk,
        riskLevel: risk.processId ? RISK_LEVEL.STANDARD : (risk.protocolId ? RISK_LEVEL.PROTOCOL : RISK_LEVEL.PROTOCOL),
        biological_entities: risk.biological_entities || [],
        applicablePatientRiskFactorIds: risk.applicablePatientRiskFactorIds || [],
        applicableEnvironmentalRiskFactorIds: risk.applicableEnvironmentalRiskFactorIds || [],
        contextualFactors: risk.contextualFactors || [],
        mitigationStrategies: risk.mitigationStrategies || [],
        potentialConsequences: risk.potentialConsequences || [],
        affectedPopulations: risk.affectedPopulations || [],
        evidenceBase: risk.evidenceBase || { guidelines: [], incidents: [], studies: [] }
      });
    } else {
      // Creating new risk
      const defaultRisk = createDefaultRisk(
        protocol?.id || null,
        protocol?.name || null,
        stepId,
        actionId
      );
      if (isStandalone) {
        defaultRisk.riskLevel = RISK_LEVEL.PROTOCOL;
        defaultRisk.organizationId = organizationId || null;
      }
      if (stepId && protocol?.steps) {
        const step = protocol.steps.find(s => s.id === stepId);
        if (step) {
          defaultRisk.stepName = step.name;
          defaultRisk.stepSequence = step.sequenceNumber;
        }
      }
      if (actionId && protocol?.steps) {
        const action = protocol.steps
          .flatMap(s => s.actions || [])
          .find(a => a.id === actionId);
        if (action) {
          defaultRisk.actionName = action.name;
          defaultRisk.actionSequence = action.sequenceNumber;
        }
      }
      setFormData(defaultRisk);
    }
  }, [risk, protocol, stepId, actionId, isStandalone, organizationId]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };
  
  const handleRPNChange = (factor, value) => {
    const numValue = parseInt(value) || 1;
    const clampedValue = Math.max(1, Math.min(10, numValue));
    
    const newFormData = {
      ...formData,
      [factor]: clampedValue
    };
    
    // Recalculate RPN and criticality using service
    try {
      const rpn = riskCalculationService.calculateRPN(
        newFormData.severity || 1, 
        newFormData.occurrence || 1, 
        newFormData.detection || 1
      );
      const riskLevel = riskCalculationService.getRiskLevel(rpn);
      
      setFormData({
        ...newFormData,
        rpn,
        criticality: riskLevel.level
      });
    } catch (error) {
      console.error('Error calculating RPN:', error);
    }
  };
  
  const handleBiologicalEntityAdd = (entityId) => {
    const entity = biological_entities.find(e => e.id === entityId);
    if (!entity) return;
    
    // Use scientific name as primary, fallback to common name
    const displayName = entity.scientificName || entity.commonName || t('unnamedEntity');
    
    const association = createBiologicalEntityAssociation(entity.id, displayName);
    
    setFormData(prev => ({
      ...prev,
      biological_entities: [...(prev.biological_entities || []), association]
    }));
  };
  
  const handleBiologicalEntityRemove = (entityId) => {
    setFormData(prev => ({
      ...prev,
      biological_entities: (prev.biological_entities || []).filter(
        entity => entity.biologicalEntityId !== entityId
      )
    }));
  };
  
  const handleBiologicalEntityUpdate = (entityId, field, value) => {
    setFormData(prev => ({
      ...prev,
      biological_entities: (prev.biological_entities || []).map(entity =>
        entity.biologicalEntityId === entityId
          ? { ...entity, [field]: value }
          : entity
      )
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.riskScenario?.trim()) {
      newErrors.riskScenario = t('riskScenarioRequiredVal');
    }
    
    if (!formData.riskDescription?.trim()) {
      newErrors.riskDescription = 'Risk description is required';
    }
    
    if (formData.severity < 1 || formData.severity > 10) {
      newErrors.severity = t('severityRange');
    }
    
    if (formData.occurrence < 1 || formData.occurrence > 10) {
      newErrors.occurrence = 'Occurrence must be between 1 and 10';
    }
    
    if (formData.detection < 1 || formData.detection > 10) {
      newErrors.detection = t('detectionRange');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      
      let savedRisk;
      if (risk) {
        // Update existing risk
        await riskService.updateRisk(risk.id, formData, userId);
        savedRisk = { ...formData, id: risk.id };
      } else {
        // Create new risk
        const riskId = await riskService.createRisk(formData, userId);
        savedRisk = { ...formData, id: riskId };
      }
      
      onSave(savedRisk);
    } catch (error) {
      console.error('Error saving risk:', error);
      setErrors({ general: t('failedToSaveRisk') });
    } finally {
      setSaving(false);
    }
  };
  
  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case CRITICALITY_LEVELS.CRITICAL: return 'critical';
      case CRITICALITY_LEVELS.HIGH: return 'high';
      case CRITICALITY_LEVELS.MEDIUM: return 'medium';
      case CRITICALITY_LEVELS.LOW: return 'low';
      default: return 'default';
    }
  };

  const handleCompleteOScoreReview = async () => {
    if (!canReview || !risk?.id || !editorUserId) return;
    setOScoreReviewSubmitting(true);
    try {
      await riskService.completeOScoreReview(risk.id, editorUserId);
      const fresh = await riskService.getRisk(risk.id);
      if (fresh) {
        setFormData((prev) => ({ ...prev, ...fresh }));
      }
    } catch (err) {
      console.error('[RiskEditor] completeOScoreReview failed:', err);
      setErrors((prev) => ({ ...prev, general: t('failedToSaveRisk') }));
    } finally {
      setOScoreReviewSubmitting(false);
    }
  };

  const showOScoreReviewBanner =
    Boolean(risk) && formData?.oScoreReviewPending === true && canView;

  return (
    <div className="risk-editor-overlay">
      <div className="risk-editor">
        <div className="risk-editor-header">
          <div>
            <h2>{risk ? 'Edit Risk' : 'Create New Risk'}</h2>
            <p>
              {protocol && `Associated with: ${protocol.name}`}
              {stepId && formData.stepName && ` → Step: ${formData.stepName}`}
              {actionId && formData.actionName && ` → Action: ${formData.actionName}`}
              {!protocol && 'Standalone risk (can be associated with protocols later)'}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={20} />
          </button>
        </div>
        
        <div className="risk-editor-content">
          {/* General Error Display */}
          {errors.general && (
            <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
              <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              {errors.general}
            </div>
          )}

          {showOScoreReviewBanner && (
            <div className="o-score-review-banner">
              <AlertTriangle size={20} className="o-score-review-banner__icon" />
              <div className="o-score-review-banner__content">
                <strong className="o-score-review-banner__title">{t('oScoreReviewBannerTitle')}</strong>
                <p className="o-score-review-banner__message">
                  {t('oScoreReviewBannerMessage', {
                    capaId: formData?.oScoreReviewCapaId || 'N/A'
                  })}
                </p>
              </div>
              {canReview && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm o-score-review-banner__action"
                  disabled={oScoreReviewSubmitting || !editorUserId}
                  onClick={handleCompleteOScoreReview}
                >
                  {oScoreReviewSubmitting ? t('common:loading') : t('markReviewed')}
                </button>
              )}
            </div>
          )}
          
          {/* Link to Standard or Protocol (QMS Phase 2 - standalone only) */}
          {isStandalone && (
            <div className="form-section">
              <h3>{t('linkTo')}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('riskLevel')}</label>
                  <select
                    value={formData.riskLevel || RISK_LEVEL.PROTOCOL}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleInputChange('riskLevel', val);
                      if (val === RISK_LEVEL.STANDARD) {
                        setFormData(prev => ({ ...prev, protocolId: null, protocolName: null, stepId: null, stepName: null, actionId: null, actionName: null, processId: prev.processId || null, processName: prev.processName || null, processCode: prev.processCode || null }));
                      } else {
                        setFormData(prev => ({ ...prev, processId: null, processName: null, processCode: null, protocolId: prev.protocolId || null, protocolName: prev.protocolName || null }));
                      }
                    }}
                  >
                    <option value={RISK_LEVEL.PROTOCOL}>{t('protocol')}</option>
                    <option value={RISK_LEVEL.STANDARD}>{t('standard')}</option>
                  </select>
                </div>
                {formData.riskLevel === RISK_LEVEL.STANDARD && (
                  <div className="form-group full-width">
                    <label>{t('selectStandard')}</label>
                    <select
                      value={formData.processId || ''}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        const proc = processes.find(p => p.id === id);
                        setFormData(prev => ({ ...prev, processId: id, processName: proc ? (proc.processName || proc.name) : null, processCode: proc ? proc.processCode : null }));
                      }}
                    >
                      <option value="">— {t('none')} —</option>
                      {(processes || []).map(p => (
                        <option key={p.id} value={p.id}>{p.processName || p.name} {p.processCode ? `(${p.processCode})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                {formData.riskLevel === RISK_LEVEL.PROTOCOL && (
                  <div className="form-group full-width">
                    <label>{t('selectProtocol')}</label>
                    <select
                      value={formData.protocolId || ''}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        const prot = (protocols || []).find(p => p.id === id);
                        setFormData(prev => ({ ...prev, protocolId: id, protocolName: prot ? prot.name : null }));
                      }}
                    >
                      <option value="">— {t('none')} —</option>
                      {(protocols || []).map(p => (
                        <option key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Relevant risk factors (QMS Phase 2 - standalone only) */}
          {isStandalone && (patientRiskFactors?.length > 0 || environmentalRiskFactors?.length > 0) && (
            <div className="form-section">
              <h3>{t('relevantRiskFactors')}</h3>
              {patientRiskFactors?.length > 0 && (
                <div className="form-group full-width">
                  <label>{t('patientRiskFactors')}</label>
                  <select
                    multiple
                    value={formData.applicablePatientRiskFactorIds || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, o => o.value);
                      handleInputChange('applicablePatientRiskFactorIds', selected);
                    }}
                    className="multi-select"
                  >
                    {patientRiskFactors.map(f => (
                      <option key={f.id} value={f.id}>{f.name} {f.category ? `(${f.category})` : ''}</option>
                    ))}
                  </select>
                  <small className="form-hint">{t('relevantRiskFactorsHint')}</small>
                </div>
              )}
              {environmentalRiskFactors?.length > 0 && (
                <div className="form-group full-width">
                  <label>{t('environmentalRiskFactors')}</label>
                  <select
                    multiple
                    value={formData.applicableEnvironmentalRiskFactorIds || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, o => o.value);
                      handleInputChange('applicableEnvironmentalRiskFactorIds', selected);
                    }}
                    className="multi-select"
                  >
                    {environmentalRiskFactors.map(f => (
                      <option key={f.id} value={f.id}>{f.name} {f.category ? `(${f.category})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Basic Information */}
          <div className="form-section">
            <h3>{t('basicInformation')}</h3>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="riskScenario">{t('riskScenarioRequired')}</label>
                <input
                  type="text"
                  id="riskScenario"
                  value={formData.riskScenario || ''}
                  onChange={(e) => handleInputChange('riskScenario', e.target.value)}
                  className={errors.riskScenario ? 'error' : ''}
                  placeholder={t('riskScenarioPlaceholder')}
                />
                {errors.riskScenario && <span className="error-text">{errors.riskScenario}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="riskType">{t('riskType')}</label>
                <select
                  id="riskType"
                  value={formData.riskType || ''}
                  onChange={(e) => handleInputChange('riskType', e.target.value)}
                  disabled={riskTypesLoading}
                >
                  <option value="">{t('selectRiskType')}</option>
                  {riskTypes.map(riskType => (
                    <option key={riskType.id} value={riskType.name}>
                      {riskType.name}
                    </option>
                  ))}
                </select>
              </div>
              
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="riskDescription">Risk Description *</label>
              <textarea
                id="riskDescription"
                value={formData.riskDescription || ''}
                onChange={(e) => handleInputChange('riskDescription', e.target.value)}
                className={errors.riskDescription ? 'error' : ''}
                rows={8}
                placeholder="Detailed description of the risk, including how it could occur and its potential impact"
              />
              {errors.riskDescription && <span className="error-text">{errors.riskDescription}</span>}
            </div>
          </div>
          
          {/* RPN Calculation */}
          <div className="form-section">
            <h3>
              <Calculator size={20} />
              {t('rpnCalculation')}
            </h3>
            
            <div className="rpn-calculator">
              <div className="rpn-factors">
                <div className="rpn-factor">
                  <label htmlFor="severity">
                    Severity (S) * 
                    <span className="rpn-value-display">{formData.severity || 5}</span>
                  </label>
                  <div className="slider-container">
                    <input
                      type="range"
                      id="severity"
                      min="1"
                      max="10"
                      value={formData.severity || 5}
                      onChange={(e) => handleRPNChange('severity', e.target.value)}
                      className={`rpn-slider ${errors.severity ? 'error' : ''}`}
                      step="1"
                    />
                    <div className="slider-labels">
                      <span>1</span>
                      <span>10</span>
                    </div>
                  </div>
                  {errors.severity && <span className="error-text">{errors.severity}</span>}
                  <div className="rpn-description">
                    {t('severityDescription')}
                  </div>
                  <div className="rpn-rationale">
                    {formData.severityRationale || t('rationalePlaceholder', { factor: 'severity' })}
                  </div>
                </div>
                
                <div className="rpn-factor">
                  <label htmlFor="occurrence">
                    {t('occurrenceLabel')}{' '}
                    <span className="rpn-value-display">{formData.occurrence || 5}</span>
                  </label>
                  <div className="slider-container">
                    <input
                      type="range"
                      id="occurrence"
                      min="1"
                      max="10"
                      value={formData.occurrence || 5}
                      onChange={(e) => handleRPNChange('occurrence', e.target.value)}
                      className={`rpn-slider ${errors.occurrence ? 'error' : ''}`}
                      step="1"
                    />
                    <div className="slider-labels">
                      <span>1</span>
                      <span>10</span>
                    </div>
                  </div>
                  {errors.occurrence && <span className="error-text">{errors.occurrence}</span>}
                  <div className="rpn-description">
                    Likelihood of risk occurring
                  </div>
                  <div className="rpn-rationale">
                    {formData.occurrenceRationale || 'Enter rationale for occurrence score'}
                  </div>
                </div>
                
                <div className="rpn-factor">
                  <label htmlFor="detection">
                    Detection (D) * 
                    <span className="rpn-value-display">{formData.detection || 5}</span>
                  </label>
                  <div className="slider-container">
                    <input
                      type="range"
                      id="detection"
                      min="1"
                      max="10"
                      value={formData.detection || 5}
                      onChange={(e) => handleRPNChange('detection', e.target.value)}
                      className={`rpn-slider ${errors.detection ? 'error' : ''}`}
                      step="1"
                    />
                    <div className="slider-labels">
                      <span>1</span>
                      <span>10</span>
                    </div>
                  </div>
                  {errors.detection && <span className="error-text">{errors.detection}</span>}
                  <div className="rpn-description">
                    {t('detectionDescription')}
                  </div>
                  <div className="rpn-rationale">
                    {formData.detectionRationale || t('rationalePlaceholder', { factor: 'detection' })}
                  </div>
                </div>
              </div>
              
              <div className="rpn-result">
                <div className="rpn-display">
                  <div className="rpn-formula">
                    RPN = S × O × D = {formData.severity || 0} × {formData.occurrence || 0} × {formData.detection || 0}
                  </div>
                  <div className="rpn-value">
                    = {formData.rpn || 0}
                  </div>
                  {(() => {
                    const riskLevel = riskCalculationService.getRiskLevel(formData.rpn || 0);
                    return (
                      <>
                        <div className={`criticality-display ${getCriticalityColor(formData.criticality || riskLevel.level)}`}>
                          {formData.criticality || riskLevel.level}
                        </div>
                        <div className="risk-action-required" style={{ color: riskLevel.color }}>
                          {riskLevel.action === 'immediate' && '⚠️ IMMEDIATE ACTION REQUIRED'}
                          {riskLevel.action === 'urgent' && '⚡ URGENT ACTION REQUIRED'}
                          {riskLevel.action === 'scheduled' && '📅 SCHEDULED REVIEW REQUIRED'}
                          {riskLevel.action === 'routine' && '✓ ROUTINE MONITORING'}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
          
          {/* Biological Entity Associations */}
          <div className="form-section">
            <h3>{t('biologicalEntityAssociations')}</h3>
            
            <div className="biological-entities-section">
              <div className="entity-selector">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBiologicalEntityAdd(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  disabled={entitiesLoading}
                >
                  {entitiesLoading ? (
                    <option value="">{t('loadingBiologicalEntities')}</option>
                  ) : biological_entities.length === 0 ? (
                    <option value="">{t('noBiologicalEntities')}</option>
                  ) : (
                    <option value="">{t('selectBiologicalEntity')}</option>
                  )}
                  {!entitiesLoading && biological_entities
                    .filter(entity => 
                      !formData.biological_entities?.some(
                        associated => associated.biologicalEntityId === entity.id
                      )
                    )
                    .map(entity => {
                      const displayName = entity.scientificName || entity.commonName || 'Unnamed Entity';
                      const fullName = entity.scientificName && entity.commonName 
                        ? `${entity.scientificName} (${entity.commonName})`
                        : displayName;
                      return (
                        <option key={entity.id} value={entity.id}>
                          {fullName} ({entity.category})
                        </option>
                      );
                    })}
                </select>
              </div>
              
              {formData.biological_entities?.length > 0 && (
                <div className="associated-entities">
                  {formData.biological_entities.map(entity => (
                    <div key={entity.biologicalEntityId} className="entity-item">
                      <div className="entity-info">
                        <span className="entity-name">🦠 {entity.biologicalEntityName}</span>
                        <select
                          value={entity.relevance}
                          onChange={(e) => handleBiologicalEntityUpdate(
                            entity.biologicalEntityId, 
                            'relevance', 
                            e.target.value
                          )}
                        >
                          <option value="Primary">{t('primary')}</option>
                          <option value="Secondary">{t('secondary')}</option>
                          <option value="Potential">{t('potential')}</option>
                        </select>
                      </div>
                      <button
                        onClick={() => handleBiologicalEntityRemove(entity.biologicalEntityId)}
                        className="btn btn-delete btn-icon"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Risk Controls (QMS Phase 2 – only when editing existing risk) */}
          {risk?.id && (
            <div className="form-section">
              <h3>{t('riskControls')}</h3>
              {controlsLoading ? (
                <p className="form-hint">{t('loadingControls')}</p>
              ) : (
                <>
                  <div className="controls-toolbar">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setEditingControlId(null);
                        setControlForm({
                          controlType: RISK_CONTROL_TYPES.PROTOCOL,
                          referenceLabel: '',
                          effectivenessTarget: '',
                          effectivenessCurrent: '',
                          controlStatus: RISK_CONTROL_STATUS.ACTIVE
                        });
                        setShowControlModal(true);
                      }}
                    >
                      <Plus size={14} />
                      {t('addControl')}
                    </button>
                  </div>
                  {controls?.length > 0 ? (
                    <div className="controls-table-wrap">
                      <table className="controls-table">
                        <thead>
                          <tr>
                            <th>{t('controlType')}</th>
                            <th>{t('controlReference')}</th>
                            <th>{t('effectivenessTarget')}</th>
                            <th>{t('effectivenessCurrent')}</th>
                            <th>{t('controlStatus')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {controls.map(c => (
                            <tr key={c.id}>
                              <td>{c.controlType}</td>
                              <td>{c.referenceLabel || '—'}</td>
                              <td>{c.effectivenessTarget != null ? `${c.effectivenessTarget}%` : '—'}</td>
                              <td>{c.effectivenessCurrent != null ? `${c.effectivenessCurrent}%` : '—'}</td>
                              <td><span className={`control-status-badge ${(c.controlStatus || '').toLowerCase().replace(/\s/g, '-')}`}>{c.controlStatus}</span></td>
                              <td>
                                <button type="button" className="btn btn-edit btn-icon" title={t('editRisk')} onClick={() => { setEditingControlId(c.id); setControlForm({ controlType: c.controlType, referenceLabel: c.referenceLabel || '', effectivenessTarget: c.effectivenessTarget ?? '', effectivenessCurrent: c.effectivenessCurrent ?? '', controlStatus: c.controlStatus || RISK_CONTROL_STATUS.ACTIVE }); setShowControlModal(true); }}><Edit size={14} /></button>
                                <button type="button" className="btn btn-delete btn-icon" title={t('deleteControl')} onClick={() => setDeleteControlConfirm(c)}><Trash2 size={14} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="form-hint">{t('noControlsYet')}</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Risk Thresholds (QMS Phase 2 – only when editing existing risk) */}
          {risk?.id && (
            <div className="form-section">
              <h3>{t('riskThresholds')}</h3>
              {thresholdsLoading ? (
                <p className="form-hint">{t('loadingThresholds')}</p>
              ) : (
                <>
                  <div className="controls-toolbar">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setEditingThresholdId(null);
                        setThresholdForm({
                          metric: THRESHOLD_METRICS.COMPLIANCE_PCT,
                          operator: THRESHOLD_OPERATORS.LTE,
                          value: '',
                          isActive: true
                        });
                        setShowThresholdModal(true);
                      }}
                    >
                      <Plus size={14} />
                      {t('addThreshold')}
                    </button>
                  </div>
                  {thresholds?.length > 0 ? (
                    <div className="controls-table-wrap">
                      <table className="controls-table">
                        <thead>
                          <tr>
                            <th>{t('thresholdMetric')}</th>
                            <th>{t('thresholdOperator')}</th>
                            <th>{t('thresholdValue')}</th>
                            <th>{t('controlStatus')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {thresholds.map(th => (
                            <tr key={th.id}>
                              <td>{THRESHOLD_METRIC_OPTIONS.find(o => o.value === th.metric)?.label || th.metric}</td>
                              <td>{th.operator}</td>
                              <td>{th.value != null ? th.value : '—'}</td>
                              <td><span className={`control-status-badge ${th.isActive ? 'active' : 'under-review'}`}>{th.isActive ? t('active') : t('inactive')}</span></td>
                              <td>
                                <button type="button" className="btn btn-edit btn-icon" title={t('editRisk')} onClick={() => { setEditingThresholdId(th.id); setThresholdForm({ metric: th.metric, operator: th.operator, value: th.value ?? '', isActive: th.isActive !== false }); setShowThresholdModal(true); }}><Edit size={14} /></button>
                                <button type="button" className="btn btn-delete btn-icon" title={t('deleteThreshold')} onClick={() => setDeleteThresholdConfirm(th)}><Trash2 size={14} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="form-hint">{t('noThresholdsYet')}</p>
                  )}
                  {violations?.length > 0 && (
                    <div className="threshold-violations">
                      <h4>{t('recentViolations')}</h4>
                      <ul>
                        {violations.slice(0, 5).map(v => (
                          <li key={v.id}>
                            {v.at?.toDate?.()?.toLocaleString?.() || (v.at && new Date(v.at.seconds * 1000).toLocaleString())} — {t('metricValue')}: {v.metricValue} ({v.actionTaken || 'logged'})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Rationale */}
          <div className="form-section">
            <h3>{t('assessmentRationale')}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="severityRationale">{t('severityRationale')}</label>
                <textarea
                  id="severityRationale"
                  value={formData.severityRationale || ''}
                  onChange={(e) => handleInputChange('severityRationale', e.target.value)}
                  rows={3}
                  placeholder={t('severityRationalePlaceholder')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="occurrenceRationale">{t('occurrenceRationale')}</label>
                <textarea
                  id="occurrenceRationale"
                  value={formData.occurrenceRationale || ''}
                  onChange={(e) => handleInputChange('occurrenceRationale', e.target.value)}
                  rows={3}
                  placeholder={t('occurrenceRationalePlaceholder')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="detectionRationale">{t('detectionRationale')}</label>
                <textarea
                  id="detectionRationale"
                  value={formData.detectionRationale || ''}
                  onChange={(e) => handleInputChange('detectionRationale', e.target.value)}
                  rows={3}
                  placeholder={t('detectionRationalePlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="risk-editor-footer">
          <button onClick={onClose} className="btn btn-cancel">
            {t('cancel')}
          </button>
          <button 
            onClick={handleSave} 
            className="btn btn-save"
            disabled={saving}
          >
            {saving ? t('saving') : (
              <>
                <Save size={16} />
                {risk ? t('updateRisk') : t('createRisk')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Control Add/Edit modal */}
      {showControlModal && (
        <div className="modal-overlay" onClick={() => setShowControlModal(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{editingControlId ? t('editControl') : t('addControl')}</h3>
            <div className="form-group">
              <label>{t('controlType')}</label>
              <select
                value={controlForm.controlType}
                onChange={e => setControlForm(prev => ({ ...prev, controlType: e.target.value }))}
              >
                {RISK_CONTROL_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('controlReference')}</label>
              <input
                type="text"
                value={controlForm.referenceLabel}
                onChange={e => setControlForm(prev => ({ ...prev, referenceLabel: e.target.value }))}
                placeholder={t('controlReferencePlaceholder')}
              />
              <p className="form-help">{t('controlReferenceHelp')}</p>
            </div>
            <div className="form-grid two-cols">
              <div className="form-group">
                <label>{t('effectivenessTarget')} (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={controlForm.effectivenessTarget}
                  onChange={e => setControlForm(prev => ({ ...prev, effectivenessTarget: e.target.value }))}
                  placeholder="—"
                />
              </div>
              <div className="form-group">
                <label>{t('effectivenessCurrent')} (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={controlForm.effectivenessCurrent}
                  onChange={e => setControlForm(prev => ({ ...prev, effectivenessCurrent: e.target.value }))}
                  placeholder="—"
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('controlStatus')}</label>
              <select
                value={controlForm.controlStatus}
                onChange={e => setControlForm(prev => ({ ...prev, controlStatus: e.target.value }))}
              >
                {RISK_CONTROL_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowControlModal(false)}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={handleSaveControl}>{editingControlId ? t('updateControl') : t('addControl')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete control confirmation */}
      {deleteControlConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteControlConfirm(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmDeleteControlTitle')}</h3>
            <p>{t('confirmDeleteControl')}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteControlConfirm(null)}>{t('cancel')}</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteControl}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Threshold Add/Edit modal */}
      {showThresholdModal && (
        <div className="modal-overlay" onClick={() => setShowThresholdModal(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{editingThresholdId ? t('editThreshold') : t('addThreshold')}</h3>
            <div className="form-group">
              <label>{t('thresholdMetric')}</label>
              <select
                value={thresholdForm.metric}
                onChange={e => setThresholdForm(prev => ({ ...prev, metric: e.target.value }))}
              >
                {THRESHOLD_METRIC_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-grid two-cols">
              <div className="form-group">
                <label>{t('thresholdOperator')}</label>
                <select
                  value={thresholdForm.operator}
                  onChange={e => setThresholdForm(prev => ({ ...prev, operator: e.target.value }))}
                >
                  {THRESHOLD_OPERATOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('thresholdValue')}</label>
                <input
                  type="number"
                  value={thresholdForm.value}
                  onChange={e => setThresholdForm(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="e.g. 80"
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={thresholdForm.isActive}
                  onChange={e => setThresholdForm(prev => ({ ...prev, isActive: e.target.checked }))}
                /> {t('thresholdActive')}
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowThresholdModal(false)}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={handleSaveThreshold}>{editingThresholdId ? t('updateThreshold') : t('addThreshold')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete threshold confirmation */}
      {deleteThresholdConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteThresholdConfirm(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmDeleteThresholdTitle')}</h3>
            <p>{t('confirmDeleteThreshold')}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteThresholdConfirm(null)}>{t('cancel')}</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteThreshold}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskEditor;
